import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import type { ProducedDraftDocument } from '@/hooks/useChat';

interface UseAutoOpenProducedDraftOptions {
  enabled?: boolean;
  onConsumed: (documentGuid?: string) => void;
  producedDraftDocument: ProducedDraftDocument | null;
  slug: string;
}

export function useAutoOpenProducedDraft({
  enabled = true,
  onConsumed,
  producedDraftDocument,
  slug,
}: UseAutoOpenProducedDraftOptions) {
  const navigate = useNavigate();
  const { showCasePartnerTab } = useSidePanel();
  const lastHandledKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !slug || !producedDraftDocument?.documentGuid) return;

    const requestKey = [
      producedDraftDocument.documentGuid,
      producedDraftDocument.version ?? 'latest',
      producedDraftDocument.generationId ?? producedDraftDocument.producedAt,
    ].join(':');

    if (lastHandledKeyRef.current === requestKey) return;
    lastHandledKeyRef.current = requestKey;

    onConsumed(producedDraftDocument.documentGuid);
    showCasePartnerTab({ activate: true, open: true });

    void navigate({
      to: '/matter/$slug/drafting/$documentGuid',
      params: {
        slug,
        documentGuid: producedDraftDocument.documentGuid,
      },
    }).finally(() => {
      window.setTimeout(() => {
        showCasePartnerTab({ activate: true, open: true });
      }, 0);
    });
  }, [enabled, navigate, onConsumed, producedDraftDocument, showCasePartnerTab, slug]);
}
